import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import useIsGlobetrotter from '../../hooks/useIsGlobetrotter.js';
import { Rating as Rating$2, Plus } from './Rating.style.js';
import cardTexts from '../Card/Card.texts.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useGlobals from '../../hooks/useGlobals.js';
import Icon from '../Icon/Icon.js';
import { UI_LOGO } from '../../icons/UI_LOGO.js';

var _excluded = ["rating", "size", "responsive"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var UI_LOGO_CIRCLE = {
  "default":
  // eslint-disable-next-line max-len
  'M16,0A16,16,0,1,0,32,16,16,16,0,0,0,16,0ZM26.12,18.73,25.3,19a34.07,34.07,0,0,1-4.39,1.51c-1.84.41-3.06.39-3.84-.07L17,20.42a1.76,1.76,0,0,1-.7-.91,2.28,2.28,0,0,1-.13-.69v-.19h0a1.35,1.35,0,0,0-.57-1.11l-.07-.05h0a3.57,3.57,0,0,0-2.09-.35,10.57,10.57,0,0,0-1.07.14l-.68.14a36.43,36.43,0,0,0-4.77,1.5c-1.3.5-2.54,1-3.56,1.46V18.2c1-.42,2.12-.89,3.31-1.35a35.4,35.4,0,0,1,4.77-1.5c1.84-.42,3.07-.39,3.85.06l.08.06a1.83,1.83,0,0,1,.74,1,1.86,1.86,0,0,1,.07.52h0v.22a1.39,1.39,0,0,0,.59,1.15l.07.05h0a3.58,3.58,0,0,0,2.09.34A6.93,6.93,0,0,0,20,18.64c.21,0,.44-.08.68-.13A34.6,34.6,0,0,0,25.06,17c.77-.29,1.44-.56,2.05-.81Zm-.82-3.58a32.34,32.34,0,0,1-4.39,1.5c-1.84.42-3.06.39-3.84-.06L17,16.53a1.8,1.8,0,0,1-.7-.91,2.34,2.34,0,0,1-.13-.7v-.2h0a1.35,1.35,0,0,0-.57-1.11l-.07,0h0a3.58,3.58,0,0,0-2.09-.34,6.83,6.83,0,0,0-1.07.14c-.22,0-.45.08-.68.13A36.68,36.68,0,0,0,6.89,15c-1.3.49-2.54,1-3.56,1.45V14.3c1-.41,2.12-.89,3.31-1.34a37.17,37.17,0,0,1,4.77-1.51c1.84-.41,3.07-.39,3.84.07l.09.05a1.84,1.84,0,0,1,.81,1.55h0v.21a1.4,1.4,0,0,0,.59,1.16l.07,0h0a3.57,3.57,0,0,0,2.09.35c.27,0,.62-.06,1.08-.14l.68-.14a34.31,34.31,0,0,0,4.39-1.5c1.61-.61,2.77-1.1,3.87-1.59l-1,2.59C27.07,14.46,26.11,14.84,25.3,15.15Z'
};

/**
 * Rating component displays a rating based on a numerical value.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {2|3|4|5|6|7|8|9|10|11} props.rating - The numerical rating value.
 * @param {'s'|'m'} [props.size='m'] - The size of the rating component.
 * @param {boolean} [props.responsive=false] - Whether the rating component is responsive.
 * @param {import('react').HTMLAttributes<HTMLDivElement>} [props.rest] - Additional props to be spread onto the button element.
 * @returns {JSX.Element|boolean} The rendered Rating component or false if rating is invalid.
 */
var Rating = function Rating(_ref) {
  var rating = _ref.rating,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'm' : _ref$size,
    _ref$responsive = _ref.responsive,
    responsive = _ref$responsive === void 0 ? false : _ref$responsive,
    rest = _objectWithoutProperties(_ref, _excluded);
  var isGlobetrotter = useIsGlobetrotter();
  var _useGlobals = useGlobals(),
    culture = _useGlobals.culture;
  var ariaLabelText = cardTexts[culture].standard;
  if (typeof rating !== 'number') {
    return false;
  }
  if (rating < 2 || rating > 11) {
    return false;
  }
  var ratings = [];
  for (var i = 0; i < Math.floor(rating / 2); i += 1) {
    ratings.push(/*#__PURE__*/jsx(Icon, {
      "aria-hidden": "true",
      path: isGlobetrotter ? UI_LOGO : UI_LOGO_CIRCLE
    }, i));
  }

  // TODO: Add aria-label with rating in text form
  var getAriaLabel = function getAriaLabel() {
    var ratingValue = Math.floor(rating / 2);
    var hasPlus = rating % 2 === 1;
    return "".concat(ariaLabelText, " ").concat(ratingValue).concat(hasPlus ? '+' : '');
  };
  return /*#__PURE__*/jsxs(Rating$2, _objectSpread(_objectSpread({
    role: "img",
    $responsive: responsive,
    $size: size,
    $hasPlus: rating % 2 === 1,
    "aria-label": getAriaLabel()
  }, rest), {}, {
    children: [ratings, rating % 2 === 1 && /*#__PURE__*/jsx(Plus, {
      "aria-hidden": "true",
      children: "+"
    })]
  }));
};
var Rating$1 = Rating;

export { Rating$1 as default };
//# sourceMappingURL=Rating.js.map
